import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PersistenceService, PersistMode } from '@barthauer/abc/core/data';
import {
  AbcAccordionComponent,
  AbcDateBoxComponent,
  AbcNumberBoxComponent,
  AbcPopupComponent,
  AbcTextBoxComponent,
  _AbcButtonComponent,
  _AbcFormComponent,
  _AbcGridComponent,
  _AbcSelectBoxComponent,
  _AbcTreeComponent,
} from '@barthauer/abc/ui-components';
import { Measure, MeasureTask } from '@barthauer/custom/xmt-models';
import {
  DxAccordionModule,
  DxButtonModule,
  DxDataGridModule,
  DxDateBoxModule,
  DxFormModule,
  DxNumberBoxModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxTreeListModule,
} from 'devextreme-angular';
import { Subscription } from 'rxjs';
import { MeasureService } from '../../../customer-space/measure/measure.service';
import { NotificationMessage, ToastType } from '../../../models/enumeration-list.model';
import { DataService } from '../../../services/data.service';
import { NotificationService } from '../../../services/notification.service';
import { SharedModule } from '../../shared.module';
import { MilestoneTaskPopupService, originOfCall } from '../milestone-task-popup.service';

@Component({
  selector: 'ubt-geods-milestone',
  templateUrl: './milestone.component.html',
  styleUrls: ['./milestone.component.scss'],
  imports: [
    AbcPopupComponent,
    _AbcFormComponent,
    DxTreeListModule,
    DxFormModule,
    DxDateBoxModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxNumberBoxModule,
    DxButtonModule,
    SharedModule,
    DxPopupModule,
    DxAccordionModule,
    DxDataGridModule,
  ],
  providers: [MilestoneTaskPopupService],
})
export class MilestoneComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('milestoneAbcPopup') milestoneAbcPopup: AbcPopupComponent;
  @ViewChild('milestoneAbcForm') milestoneAbcForm: _AbcFormComponent;
  @Input() canManageMilestones: boolean;
  @Output() updateGantt = new EventEmitter<boolean>();

  public currentMeasure: Measure = new Measure(null);
  public measureTask: MeasureTask = new MeasureTask(null);
  public measureTaskId: string;

  public isUpdatingTask = false;

  private origin!: string;

  public resources!: (key: string, ...value: string[]) => string;

  public submitButtonOptions!: any;
  public cancelButtonOptions!: any;

  private subscriptions: Subscription[] = [];

  constructor(
    public dataService: DataService,
    private persistenceService: PersistenceService,
    private measureService: MeasureService,
    private notificationService: NotificationService,
    public milestoneTaskPopupService: MilestoneTaskPopupService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.resources = this.dataService.res;
    this.cancelButtonOptions = {
      text: this.resources('Ubt-CreateMeasure-Cancel'),
      useSubmitBehavior: false,
      onClick: () => this.clearWindow(),
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.currentMeasure.MeasureTasks) {
      this.prepareAllowedMeasureTask();
    } else {
      this.currentMeasure.MeasureTasks = [];
    }
    this.cdr.markForCheck();
  }

  private prepareAllowedMeasureTask() {
    this.currentMeasure.MeasureTasks.sort((a, b) => a.Label.localeCompare(b.Label));
  }

  public persistMeasureTask(event) {
    if (this.isUpdatingTask === false) {
      event.preventDefault();
      this.persistTask(PersistMode.Insert);
    } else {
      event.preventDefault();
      this.persistTask(PersistMode.Update);
    }
    this.cdr.markForCheck();
  }

  private persistTask(mode: PersistMode): void {
    if (!this.currentMeasure.MeasureTasks) {
      this.currentMeasure.MeasureTasks = [];
    }
    this.measureTask.StartDate = this.measureTask.EndDate;
    this.subscriptions.push(
      this.persistenceService
        .executePersistObjectQuery(
          this.milestoneTaskPopupService.preparePersistTask(
            this.currentMeasure,
            this.measureTask,
            this.measureTaskId,
            mode
          )
        )
        .subscribe(
          (task: any) => {
            if (mode === PersistMode.Insert) {
              this.measureTask.Id = task.Id;
              this.measureTask = new MeasureTask(this.measureTask);

              if (this.origin === originOfCall.Gantt) {
                this.updateGantt.next(true);
              } else {
                this.currentMeasure.MeasureTasks.push(this.measureTask);
              }
              this.dataService.updateGridData(this.measureTask);
              this.cdr.markForCheck();
            } else {
              const taskIndex = this.currentMeasure.MeasureTasks.findIndex((item) => item.Id === task.Id);
              this.currentMeasure.MeasureTasks[taskIndex] = this.measureTask;
            }
            this.measureService.realTimecalculateTotalTime(true);
            this.clearWindow();
            this.cdr.markForCheck();
            this.notificationService.callNotification({
              message: NotificationMessage.ValidInsertMessage,
              type: ToastType.SUCCESS,
            });
          },
          (error) => {
            this.notificationService.callNotification({
              message: NotificationMessage.Error,
              type: ToastType.ERROR,
            });
          },
          () => {
            this.measureTask = new MeasureTask(null);
          }
        )
    );
  }

  public showPopup(
    currentMeasure: Measure,
    measureTask: MeasureTask,
    isUpdatingTask: boolean,
    origin: originOfCall
  ): void {
    this.currentMeasure = currentMeasure;
    this.measureTask = measureTask;
    this.measureTask.Type = 0;
    this.measureTaskId = measureTask ? measureTask.Id : undefined;
    this.isUpdatingTask = isUpdatingTask;
    this.origin = origin;

    this.milestoneAbcPopup.title = this.resources(
      'Ubt-MeasureTask-' + (this.isUpdatingTask ? 'Update' : 'Create') + '-Milestone'
    );

    this.submitButtonOptions = {
      text: this.resources('Ubt-CreateMeasure-' + (this.isUpdatingTask ? 'Update' : 'Create')),
      useSubmitBehavior: true,
      disabled: !this.canManageMilestones,
    };
    this.cdr.markForCheck();
    this.milestoneAbcPopup.instance.show();
  }

  public clearWindow = (clearMeasureTask: boolean = true): void => {
    if (clearMeasureTask) {
      this.measureTask = new MeasureTask(null);
    }
    this.milestoneAbcPopup.instance.hide();
  };

  public resetValues = (): void => {
    this.milestoneAbcForm?.instance.resetValues();
  };

  public ngOnDestroy(): void {
    this.subscriptions.map((subscription) => subscription.unsubscribe());
  }
}
